import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { ArrowLeft, MapPin, Clock, Loader2, X, Plus, Search, CalendarDays, ListTodo, ChevronRight, Download, Link2, Pin, PinOff, SlidersHorizontal, Sparkles, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { US_STATES, DEFAULT_STATE } from '../utils/states';
import { SmartCityAutocomplete } from '../components/SmartCityAutocomplete';
import { CityPill, CityManager } from '../components/CityManager';
import CalendarLayers from '../components/CalendarLayers';
import FindSimilarEvents from '../components/FindSimilarEvents';
import LanguageSelector from '../components/LanguageSelector';
import CategorySelector from '../components/CategorySelector';
import GuestSignupBanner from '../components/GuestSignupBanner';
import GuestActionModal from '../components/GuestActionModal';
import { GUEST_DEMO_DATA } from '../data/guestDemoData';
import SidebarFilterContent from '../components/calendar/SidebarFilterContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PlanBlox Logo
const PlanBloxLogo = ({ className = "h-8 w-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_b5e1c113-3c1b-4c22-aa5b-d15e9e791e88/artifacts/tf4zn6ok_App%20Icon%201%20.png"
    alt="PlanBlox"
    className={className}
  />
);

const CalendarView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, getAuthHeaders, isGuestMode } = useAuth();
  const [publishedEvents, setPublishedEvents] = useState([]);
  const [privateEvents, setPrivateEvents] = useState([]);
  const [upcomingPrivateEvents, setUpcomingPrivateEvents] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPrivateDate, setSelectedPrivateDate] = useState(null);
  
  // Private event detail dialog
  const [selectedPrivateEvent, setSelectedPrivateEvent] = useState(null);
  const [privateDetailOpen, setPrivateDetailOpen] = useState(false);
  
  // Day View state
  const [dayViewOpen, setDayViewOpen] = useState(false);
  const [dayViewDate, setDayViewDate] = useState(null);
  
  // State filter - default to user's state or CA
  const [selectedState, setSelectedState] = useState(user?.state || DEFAULT_STATE);
  
  // Primary city - defaults to user's city (shown in grey until overridden)
  const [primaryCity, setPrimaryCity] = useState(user?.city || '');
  const [isPrimaryCityOverridden, setIsPrimaryCityOverridden] = useState(false);
  
  // Additional cities (up to 7)
  const [additionalCities, setAdditionalCities] = useState([]);
  const [showAddCity, setShowAddCity] = useState(false);
  const [newCityState, setNewCityState] = useState(selectedState);

  // Calendar Layers state
  const [publishedFilter, setPublishedFilter] = useState('all'); // 'all' | 'mine'
  const [showMyHolds, setShowMyHolds] = useState(true);
  const [showAllHolds, setShowAllHolds] = useState(false);
  const [showPrivateOverlay, setShowPrivateOverlay] = useState(false);
  const [holdsOverlay, setHoldsOverlay] = useState([]);
  const [myPublishedEvents, setMyPublishedEvents] = useState([]);
  
  // View mode: month or week
  const [viewMode, setViewMode] = useState('month');
  
  // Find Similar Events state
  const [findSimilarOpen, setFindSimilarOpen] = useState(false);
  
  // Current calendar month for holds overlay
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Category filter
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Mobile filter sheet
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Event search
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const [typeSearchQuery, setTypeSearchQuery] = useState('');
  const [searchTimeRange, setSearchTimeRange] = useState('1month');
  const [searchActive, setSearchActive] = useState(false);

  // Guest action modal for calendar
  const [calGuestModalOpen, setCalGuestModalOpen] = useState(false);

  // Pinned dates tray
  const [pinnedDates, setPinnedDates] = useState(() => {
    const saved = localStorage.getItem('planblox-pinned-dates');
    return saved ? JSON.parse(saved) : [];
  });
  const [quickActionDate, setQuickActionDate] = useState(null);
  const [quickActionPos, setQuickActionPos] = useState({ x: 0, y: 0 });

  // Get all cities to filter by
  const getAllFilterCities = useCallback(() => {
    const cities = [];
    if (primaryCity) {
      cities.push({ city: primaryCity, state: selectedState });
    }
    cities.push(...additionalCities);
    return cities;
  }, [primaryCity, selectedState, additionalCities]);

  const fetchPublishedEvents = useCallback(async () => {
    if (isGuestMode) {
      // Use demo calendar data in guest mode
      setPublishedEvents(GUEST_DEMO_DATA.calendarEvents || []);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const cities = getAllFilterCities();
      
      // Fetch events for all selected cities
      if (cities.length === 0) {
        // If no city selected, fetch by state only
        const url = `${API}/events/published/all?state=${selectedState}`;
        const response = await axios.get(url, { headers: getAuthHeaders() });
        setPublishedEvents(response.data);
      } else {
        // Fetch events for each city and combine
        const allEvents = [];
        const seenIds = new Set();
        
        for (const cityObj of cities) {
          const url = `${API}/events/published/all?state=${cityObj.state}&city=${encodeURIComponent(cityObj.city)}`;
          try {
            const response = await axios.get(url, { headers: getAuthHeaders() });
            for (const event of response.data) {
              if (!seenIds.has(event.id)) {
                seenIds.add(event.id);
                allEvents.push(event);
              }
            }
          } catch (err) {
            console.error(`Failed to fetch events for ${cityObj.city}:`, err);
          }
        }
        
        setPublishedEvents(allEvents);
      }
    } catch (error) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedState, getAllFilterCities, isGuestMode]);

  // Fetch private events and lists
  const fetchPrivateEvents = useCallback(async () => {
    if (isGuestMode) {
      // Use demo private events in guest mode
      setPrivateEvents(GUEST_DEMO_DATA.calendarPrivateEvents || []);
      setUpcomingPrivateEvents(GUEST_DEMO_DATA.calendarPrivateEvents || []);
      setLists([]);
      return;
    }
    try {
      const [eventsRes, upcomingRes, listsRes] = await Promise.all([
        axios.get(`${API}/private-events`, { headers: getAuthHeaders() }),
        axios.get(`${API}/private-events/upcoming?limit=10`, { headers: getAuthHeaders() }),
        axios.get(`${API}/lists`, { headers: getAuthHeaders() })
      ]);
      setPrivateEvents(eventsRes.data);
      setUpcomingPrivateEvents(upcomingRes.data);
      setLists(listsRes.data);
    } catch (error) {
      console.error('Failed to load private events:', error);
    }
  }, [getAuthHeaders, isGuestMode]);

  useEffect(() => {
    fetchPublishedEvents();
    fetchPrivateEvents();
  }, [fetchPublishedEvents, fetchPrivateEvents]);

  // Fetch holds overlay data
  const fetchHoldsOverlay = useCallback(async () => {
    if (isGuestMode) {
      setHoldsOverlay([]);
      return;
    }
    if (!showMyHolds && !showAllHolds) {
      setHoldsOverlay([]);
      return;
    }
    
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const cities = getAllFilterCities();
      const citiesParam = cities.map(c => c.city).join(',');
      
      const params = new URLSearchParams({
        month: String(month),
        year: String(year),
        include_my_holds: String(showMyHolds),
        include_all_holds: String(showAllHolds),
      });
      
      if (citiesParam) params.append('cities', citiesParam);
      if (selectedState) params.append('state', selectedState);
      
      const response = await axios.get(
        `${API}/calendar/holds-overlay?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      
      setHoldsOverlay(response.data.holds || []);
    } catch (error) {
      console.error('Failed to fetch holds overlay:', error);
    }
  }, [showMyHolds, showAllHolds, currentMonth, getAllFilterCities, selectedState, getAuthHeaders, isGuestMode]);

  // Fetch my published events for "Mine Only" filter
  const fetchMyPublishedEvents = useCallback(async () => {
    if (isGuestMode) {
      setMyPublishedEvents([]);
      return;
    }
    if (publishedFilter !== 'mine') {
      setMyPublishedEvents([]);
      return;
    }
    
    try {
      const cities = getAllFilterCities();
      const citiesParam = cities.map(c => c.city).join(',');
      
      const params = new URLSearchParams();
      if (citiesParam) params.append('cities', citiesParam);
      if (selectedState) params.append('state', selectedState);
      
      const response = await axios.get(
        `${API}/events/published/mine?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      
      setMyPublishedEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch my published events:', error);
    }
  }, [publishedFilter, getAllFilterCities, selectedState, getAuthHeaders, isGuestMode]);

  // Fetch holds when month/filters change
  useEffect(() => {
    fetchHoldsOverlay();
  }, [fetchHoldsOverlay]);

  // Fetch my events when filter changes
  useEffect(() => {
    fetchMyPublishedEvents();
  }, [fetchMyPublishedEvents]);

  // Handle primary city change
  const handlePrimaryCityChange = (city, state) => {
    if (city) {
      setPrimaryCity(city);
      setSelectedState(state);
      setIsPrimaryCityOverridden(true);
    } else {
      setPrimaryCity('');
    }
  };

  // Handle state change - reset primary city to user default
  const handleStateChange = (state) => {
    setSelectedState(state);
    setNewCityState(state); // Update add city state when main state changes
    // If user has a city in this state, use it
    if (user?.state === state && user?.city) {
      setPrimaryCity(user.city);
      setIsPrimaryCityOverridden(false);
    } else {
      setPrimaryCity('');
      setIsPrimaryCityOverridden(false);
    }
  };

  // Add additional city
  const handleAddCity = (city, state) => {
    if (!city || !state) return;
    
    // Check if already added
    const exists = additionalCities.some(c => c.city === city && c.state === state);
    if (exists) {
      toast.error('City already added');
      return;
    }
    
    // Check if it's the same as primary
    if (city === primaryCity && state === selectedState) {
      toast.error('This is already your primary city');
      return;
    }
    
    if (additionalCities.length >= 7) {
      toast.error('Maximum 7 additional cities allowed');
      return;
    }
    
    setAdditionalCities([...additionalCities, { city, state }]);
    setShowAddCity(false);
    setNewCityState(selectedState);
  };

  // Remove additional city
  const handleRemoveCity = (index) => {
    setAdditionalCities(additionalCities.filter((_, i) => i !== index));
  };

  // Clear all additional cities
  const clearAllAdditionalCities = () => {
    setAdditionalCities([]);
  };

  // Get dates with events as string set for fast lookup
  const eventDateStrings = new Set(publishedEvents.map(e => e.publishedDay));
  
  // Matcher function for highlighting event dates
  const hasEventMatcher = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    // Use filtered events based on publishedFilter and category
    let eventsToShow = publishedFilter === 'mine' ? myPublishedEvents : publishedEvents;
    if (selectedCategory !== 'all') {
      eventsToShow = eventsToShow.filter(e => e.category === selectedCategory);
    }
    return eventsToShow.some(e => e.publishedDay === dateStr);
  };

  // Holds overlay matcher
  const hasHoldMatcher = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return holdsOverlay.some(h => h.date === dateStr);
  };

  // User's hold matcher
  const hasUserHoldMatcher = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return holdsOverlay.some(h => h.date === dateStr && h.hasUserHold);
  };

  // Filter private events by selected city/state
  const getFilteredPrivateEvents = useCallback(() => {
    const cities = getAllFilterCities();
    
    if (cities.length === 0) {
      // Filter by state only
      return privateEvents.filter(e => e.state === selectedState);
    }
    
    // Filter by any of the selected cities
    return privateEvents.filter(e => 
      cities.some(c => c.city === e.city && c.state === e.state)
    );
  }, [privateEvents, selectedState, getAllFilterCities]);

  const filteredPrivateEvents = getFilteredPrivateEvents();

  // Get private event dates
  const privateEventDateStrings = new Set(
    filteredPrivateEvents.filter(e => e.date).map(e => e.date)
  );

  // Matcher for private events
  const hasPrivateEventMatcher = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return privateEventDateStrings.has(dateStr);
  };
  
  // Get events for selected date (use filtered events)
  // Event search result count (null = no search performed yet)
  const [searchResultCount, setSearchResultCount] = useState(null);

  // Handle the "Search Events" button click — tallies all filter inputs
  const handleSearchEvents = () => {
    const filtered = getFilteredPublishedEvents();
    setSearchResultCount(filtered.length);
    setSearchActive(true);
    toast.success(`${filtered.length} event${filtered.length !== 1 ? 's' : ''} found`);
  };

  // Get filtered events applying category, search query, type, and time range
  const getFilteredPublishedEvents = () => {
    let events = publishedFilter === 'mine' ? myPublishedEvents : publishedEvents;
    if (selectedCategory !== 'all') {
      events = events.filter(e => e.category === selectedCategory);
    }
    // Apply event name search filter (always active as user types)
    if (eventSearchQuery.trim()) {
      const query = eventSearchQuery.trim().toLowerCase();
      events = events.filter(e => {
        const name = (e.event_name || e.title || '').toLowerCase();
        return name.includes(query);
      });
    }
    // Apply type search filter (matches against tags, description, category name)
    if (typeSearchQuery.trim()) {
      const typeQuery = typeSearchQuery.trim().toLowerCase();
      const typeTerms = typeQuery.split(',').map(t => t.trim()).filter(Boolean);
      events = events.filter(e => {
        const searchable = [
          e.event_name || e.title || '',
          e.description || '',
          e.category_info?.name || '',
          ...(e.tags || [])
        ].join(' ').toLowerCase();
        return typeTerms.some(term => searchable.includes(term));
      });
    }
    // Apply time range filter
    const now = new Date();
    const rangeEnd = new Date(now);
    if (searchTimeRange === '1month') rangeEnd.setMonth(rangeEnd.getMonth() + 1);
    else if (searchTimeRange === '3months') rangeEnd.setMonth(rangeEnd.getMonth() + 3);
    else if (searchTimeRange === '6months') rangeEnd.setMonth(rangeEnd.getMonth() + 6);
    else if (searchTimeRange === '1year') rangeEnd.setFullYear(rangeEnd.getFullYear() + 1);
    const rangeEndStr = rangeEnd.toISOString().split('T')[0];
    events = events.filter(e => {
      const day = e.publishedDay;
      if (!day) return true;
      return day <= rangeEndStr;
    });
    return events;
  };

  const selectedDateEvents = selectedDate 
    ? getFilteredPublishedEvents().filter(e => e.publishedDay === selectedDate.toISOString().split('T')[0])
    : [];

  // Get private events for selected date
  const selectedPrivateDateEvents = selectedPrivateDate
    ? filteredPrivateEvents.filter(e => e.date === selectedPrivateDate.toISOString().split('T')[0])
    : [];

  // Get private events for Day View date
  const getDayViewEvents = () => {
    if (!dayViewDate) return [];
    const dateStr = dayViewDate.toISOString().split('T')[0];
    return filteredPrivateEvents.filter(e => e.date === dateStr);
  };

  const dayViewEvents = getDayViewEvents();

  // Count events by city
  const getEventCountText = () => {
    const cities = getAllFilterCities();
    if (cities.length === 0) {
      return `${publishedEvents.length} event${publishedEvents.length !== 1 ? 's' : ''} in ${selectedState}`;
    } else if (cities.length === 1) {
      return `${publishedEvents.length} event${publishedEvents.length !== 1 ? 's' : ''} in ${cities[0].city}, ${cities[0].state}`;
    } else {
      return `${publishedEvents.length} event${publishedEvents.length !== 1 ? 's' : ''} across ${cities.length} cities`;
    }
  };

  // Handle private event click
  const handlePrivateEventClick = (event) => {
    setSelectedPrivateEvent(event);
    setPrivateDetailOpen(true);
  };

  // Open Day View for a date
  const openDayView = (date) => {
    setDayViewDate(date);
    setDayViewOpen(true);
  };

  // Handle private calendar date click - open Day View
  const handlePrivateDateClick = (date) => {
    setSelectedPrivateDate(date);
    if (date) {
      openDayView(date);
    }
  };

  // Get list by ID for checklist preview
  const getListById = (listId) => {
    return lists.find(l => l.id === listId);
  };

  // Unified selected date for both public and private
  const handleUnifiedDateClick = (date, e) => {
    setSelectedDate(date);
    setSelectedPrivateDate(date);
    setDayViewDate(date);
    
    // Show quick action menu (only for authenticated users, not guests)
    if (!isGuestMode && e) {
      const rect = e.currentTarget.getBoundingClientRect();
      setQuickActionPos({ x: Math.min(rect.left, window.innerWidth - 200), y: rect.bottom + 4 });
      setQuickActionDate(date);
    }
  };

  const togglePinDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    setPinnedDates(prev => {
      const updated = prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr];
      localStorage.setItem('planblox-pinned-dates', JSON.stringify(updated));
      return updated;
    });
  };

  const clearPinnedDates = () => {
    setPinnedDates([]);
    localStorage.removeItem('planblox-pinned-dates');
  };

  const navigateToCreateWithDate = (date, type) => {
    const dateStr = date.toISOString().split('T')[0];
    navigate(`/create?prefillDate=${dateStr}&prefillType=${type}`);
  };

  // Get all events (public + private + holds) for a specific date
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const publicEvts = getFilteredPublishedEvents().filter(e => e.publishedDay === dateStr);
    const privateEvts = showPrivateOverlay ? filteredPrivateEvents.filter(e => e.date === dateStr) : [];
    const holdEvts = holdsOverlay.filter(h => h.date === dateStr);
    return { public: publicEvts, private: privateEvts, holds: holdEvts };
  };

  // Get dot indicators for a date
  const getDateDots = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const dots = [];
    const publicEvts = getFilteredPublishedEvents().filter(e => e.publishedDay === dateStr);
    const privateEvts = filteredPrivateEvents.filter(e => e.date === dateStr);
    const holdEvts = holdsOverlay.filter(h => h.date === dateStr);
    const userHoldEvts = holdsOverlay.filter(h => h.date === dateStr && h.hasUserHold);
    if (publicEvts.length > 0) dots.push({ color: 'bg-emerald-500', count: publicEvts.length });
    if (userHoldEvts.length > 0) dots.push({ color: 'bg-blue-500', count: userHoldEvts.length });
    else if (holdEvts.length > 0) dots.push({ color: 'bg-amber-500', count: holdEvts.length });
    if (privateEvts.length > 0) dots.push({ color: 'bg-violet-500', count: privateEvts.length });
    return dots;
  };

  // Get week dates for week view
  const getWeekDates = (baseDate) => {
    const start = new Date(baseDate);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  };

  // Get selected date's events for the right sidebar
  const sidebarDate = selectedDate || new Date();
  const sidebarDateStr = sidebarDate.toISOString().split('T')[0];
  const sidebarPublicEvents = getFilteredPublishedEvents().filter(e => e.publishedDay === sidebarDateStr);
  const sidebarPrivateEvents = filteredPrivateEvents.filter(e => e.date === sidebarDateStr);
  const sidebarHolds = holdsOverlay.filter(h => h.date === sidebarDateStr);

  // Month grid helper — accepts optional year/month for 3-month view
  const getMonthGrid = (yearOverride, monthOverride) => {
    const year = yearOverride ?? currentMonth.getFullYear();
    const month = monthOverride ?? currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay();
    const days = [];
    // Previous month trailing days
    for (let i = 0; i < startOffset; i++) {
      const d = new Date(year, month, -startOffset + i + 1);
      days.push({ date: d, isCurrentMonth: false });
    }
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    // Next month leading days to fill grid
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }


  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  return (
    <div className="min-h-screen lg:h-[calc(100vh-4rem)] bg-slate-50 dark:bg-slate-950 flex flex-col pb-16 lg:pb-0" data-testid="calendar-view">
      <GuestSignupBanner />
      {/* Top Header */}
      <header className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between shrink-0 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/hub')} data-testid="back-to-hub-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Hub
          </Button>
          <div className="hidden sm:block h-5 w-px bg-slate-200 dark:bg-slate-700" />
          <div className="hidden sm:flex items-center gap-2">
            <PlanBloxLogo className="h-7 w-7 rounded-lg" />
            <span className="text-sm font-bold text-slate-900 dark:text-slate-100 tracking-tight">Event Calendar</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/directory')} data-testid="header-bell-btn">
            <Bell className="h-4 w-4" />
          </Button>
          <LanguageSelector variant="icon" />
          {/* iCal Export & Subscribe */}
          {!isGuestMode && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/me/calendar/ical`, {
                      headers: getAuthHeaders(),
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/calendar' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'planblox-calendar.ics';
                    a.click();
                    window.URL.revokeObjectURL(url);
                    toast.success('Calendar exported');
                  } catch {
                    toast.error('Failed to export calendar');
                  }
                }}
                data-testid="export-ical-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1" />
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => {
                  const token = localStorage.getItem('token');
                  const baseUrl = process.env.REACT_APP_BACKEND_URL;
                  const webcalUrl = `${baseUrl}/api/me/calendar/ical?token=${token}`.replace('https://', 'webcal://').replace('http://', 'webcal://');
                  navigator.clipboard.writeText(webcalUrl);
                  toast.success('Subscribe link copied! Paste it in Google Calendar or Apple Calendar to sync.');
                }}
                data-testid="subscribe-ical-btn"
              >
                <Link2 className="h-3.5 w-3.5 mr-1" />
                Subscribe
              </Button>
            </div>
          )}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5" data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode('3month')}
              className={`px-2 sm:px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === '3month' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
              data-testid="3month-view-toggle"
            >
              3 Mo
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-2 sm:px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'month' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
              data-testid="month-view-toggle"
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-2 sm:px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'week' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
              data-testid="week-view-toggle"
            >
              Week
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Filter Sheet */}
      <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
        <SheetContent side="left" className="w-[300px] sm:w-[340px] overflow-y-auto p-5" data-testid="mobile-filter-sheet">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-base">Filters & Layers</SheetTitle>
            <SheetDescription className="text-xs">Refine your calendar view</SheetDescription>
          </SheetHeader>
          <SidebarFilterContent
            selectedState={selectedState}
            handleStateChange={handleStateChange}
            primaryCity={primaryCity}
            handlePrimaryCityChange={handlePrimaryCityChange}
            isPrimaryCityOverridden={isPrimaryCityOverridden}
            user={user}
            additionalCities={additionalCities}
            handleRemoveCity={handleRemoveCity}
            showAddCity={showAddCity}
            setShowAddCity={setShowAddCity}
            newCityState={newCityState}
            setNewCityState={setNewCityState}
            handleAddCity={handleAddCity}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            publishedFilter={publishedFilter}
            setPublishedFilter={setPublishedFilter}
            showMyHolds={showMyHolds}
            setShowMyHolds={setShowMyHolds}
            showAllHolds={showAllHolds}
            setShowAllHolds={setShowAllHolds}
            showPrivateOverlay={showPrivateOverlay}
            setShowPrivateOverlay={setShowPrivateOverlay}
            setFindSimilarOpen={(v) => { setFindSimilarOpen(v); setFilterSheetOpen(false); }}
            eventSearchQuery={eventSearchQuery}
            setEventSearchQuery={setEventSearchQuery}
            typeSearchQuery={typeSearchQuery}
            setTypeSearchQuery={setTypeSearchQuery}
            searchTimeRange={searchTimeRange}
            setSearchTimeRange={setSearchTimeRange}
            onSearchEvents={() => { handleSearchEvents(); setFilterSheetOpen(false); }}
            filteredCount={searchResultCount}
            isGuestMode={isGuestMode}
            onGuestAction={() => { setFilterSheetOpen(false); setCalGuestModalOpen(true); }}
            navigate={navigate}
          />
        </SheetContent>
      </Sheet>

      {/* 2-Column Layout: Filters + Calendar & Events */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-0 lg:overflow-hidden">

        {/* LEFT SIDEBAR — Always visible */}
        <aside className="lg:col-span-3 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 lg:p-5 lg:overflow-y-auto max-h-[280px] lg:max-h-none overflow-y-auto" data-testid="filter-sidebar">
          <SidebarFilterContent
            selectedState={selectedState}
            handleStateChange={handleStateChange}
            primaryCity={primaryCity}
            handlePrimaryCityChange={handlePrimaryCityChange}
            isPrimaryCityOverridden={isPrimaryCityOverridden}
            user={user}
            additionalCities={additionalCities}
            handleRemoveCity={handleRemoveCity}
            showAddCity={showAddCity}
            setShowAddCity={setShowAddCity}
            newCityState={newCityState}
            setNewCityState={setNewCityState}
            handleAddCity={handleAddCity}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            publishedFilter={publishedFilter}
            setPublishedFilter={setPublishedFilter}
            showMyHolds={showMyHolds}
            setShowMyHolds={setShowMyHolds}
            showAllHolds={showAllHolds}
            setShowAllHolds={setShowAllHolds}
            showPrivateOverlay={showPrivateOverlay}
            setShowPrivateOverlay={setShowPrivateOverlay}
            setFindSimilarOpen={setFindSimilarOpen}
            eventSearchQuery={eventSearchQuery}
            setEventSearchQuery={setEventSearchQuery}
            typeSearchQuery={typeSearchQuery}
            setTypeSearchQuery={setTypeSearchQuery}
            searchTimeRange={searchTimeRange}
            setSearchTimeRange={setSearchTimeRange}
            onSearchEvents={handleSearchEvents}
            filteredCount={searchResultCount}
            isGuestMode={isGuestMode}
            onGuestAction={() => setCalGuestModalOpen(true)}
            navigate={navigate}
          />
        </aside>

        {/* MAIN — Calendar Grid + Events Below */}
        <main className="lg:col-span-9 bg-slate-50 dark:bg-slate-950 p-4 lg:p-5 lg:overflow-y-auto" data-testid="calendar-main">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - (viewMode === '3month' ? 3 : 1)))} className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors text-slate-600 dark:text-slate-400" data-testid="cal-prev-month">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="text-center">
              <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {viewMode === '3month'
                  ? `${monthNames[currentMonth.getMonth()]} – ${monthNames[(currentMonth.getMonth() + 2) % 12]} ${currentMonth.getFullYear()}`
                  : `${monthNames[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`
                }
              </h2>
              <button onClick={() => { setCurrentMonth(new Date()); handleUnifiedDateClick(new Date()); }} className="text-[10px] text-emerald-600 dark:text-emerald-400 hover:underline">Today</button>
            </div>
            <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + (viewMode === '3month' ? 3 : 1)))} className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors text-slate-600 dark:text-slate-400" data-testid="cal-next-month">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {viewMode !== '3month' && (
            <div className="grid grid-cols-7 mb-1">
              {dayNames.map(d => (
                <div key={d} className="text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 py-1">{d}</div>
              ))}
            </div>
          )}

          {viewMode === '3month' ? (
            <div className="flex-1 space-y-4 overflow-y-auto" data-testid="three-month-view">
              {[0, 1, 2].map(offset => {
                const m = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1);
                const mYear = m.getFullYear();
                const mMonth = m.getMonth();
                const grid = getMonthGrid(mYear, mMonth);
                return (
                  <div key={offset} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900">
                    <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                      <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{monthNames[mMonth]} {mYear}</h3>
                    </div>
                    <div className="grid grid-cols-7">
                      {dayNames.map(d => (
                        <div key={d} className="text-center text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 py-1">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7">
                      {grid.map(({ date, isCurrentMonth }, idx) => {
                        const dateStr = date.toISOString().split('T')[0];
                        const dots = getDateDots(date);
                        const isSelected = selectedDate && selectedDate.toISOString().split('T')[0] === dateStr;
                        const isToday = dateStr === todayStr;
                        return (
                          <button key={idx} onClick={(e) => handleUnifiedDateClick(date, e)} className={`relative min-h-[48px] p-1 border-b border-r border-slate-100 dark:border-slate-800 text-left transition-all hover:bg-slate-50 dark:hover:bg-slate-800 ${!isCurrentMonth ? 'bg-slate-50/50 dark:bg-slate-950/50' : ''} ${isSelected ? 'bg-emerald-50 dark:bg-emerald-950/30 ring-2 ring-inset ring-emerald-500' : ''}`}>
                            <span className={`text-[11px] font-medium ${!isCurrentMonth ? 'text-slate-300 dark:text-slate-600' : isToday ? 'bg-emerald-600 text-white w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px]' : 'text-slate-700 dark:text-slate-300'}`}>{date.getDate()}</span>
                            {dots.length > 0 && (
                              <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex items-center gap-px">
                                {dots.map((dot, di) => (
                                  <span key={di} className={`w-1 h-1 rounded-full ${dot.color}`} />
                                ))}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : viewMode === 'month' ? (
            <div className="grid grid-cols-7 flex-1 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900">
              {getMonthGrid().map(({ date, isCurrentMonth }, idx) => {
                const dateStr = date.toISOString().split('T')[0];
                const dots = getDateDots(date);
                const isSelected = selectedDate && selectedDate.toISOString().split('T')[0] === dateStr;
                const isToday = dateStr === todayStr;
                return (
                  <button key={idx} onClick={(e) => handleUnifiedDateClick(date, e)} className={`relative min-h-[56px] lg:min-h-[64px] p-1 border-b border-r border-slate-100 dark:border-slate-800 text-left transition-all hover:bg-slate-50 dark:hover:bg-slate-800 ${!isCurrentMonth ? 'bg-slate-50/50 dark:bg-slate-950/50' : ''} ${isSelected ? 'bg-emerald-50 dark:bg-emerald-950/30 ring-2 ring-inset ring-emerald-500' : ''}`} data-testid={`calendar-date-${dateStr}`}>
                    <span className={`text-xs font-medium ${!isCurrentMonth ? 'text-slate-300 dark:text-slate-600' : isToday ? 'bg-emerald-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center' : 'text-slate-700 dark:text-slate-300'}`}>{date.getDate()}</span>
                    {pinnedDates.includes(dateStr) && (
                      <Pin className="absolute top-1 right-1 h-3 w-3 text-blue-500" />
                    )}
                    {dots.length > 0 && (
                      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-0.5">
                        {dots.map((dot, di) => (
                          <div key={di} className="flex items-center gap-px">
                            <span className={`w-1.5 h-1.5 rounded-full ${dot.color}`} />
                            {dot.count > 1 && <span className="text-[8px] text-slate-400 dark:text-slate-500">{dot.count}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex-1 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900">
              <div className="grid grid-cols-7">
                {getWeekDates(currentMonth).map((date, idx) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const dots = getDateDots(date);
                  const isSelected = selectedDate && selectedDate.toISOString().split('T')[0] === dateStr;
                  const isToday = dateStr === todayStr;
                  const dayEvents = getEventsForDate(date);
                  return (
                    <button key={idx} onClick={(e) => handleUnifiedDateClick(date, e)} className={`min-h-[300px] p-2 border-r border-slate-100 dark:border-slate-800 text-left transition-all hover:bg-slate-50 dark:hover:bg-slate-800 ${isSelected ? 'bg-emerald-50 dark:bg-emerald-950/30 ring-2 ring-inset ring-emerald-500' : ''}`} data-testid={`week-date-${dateStr}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-medium ${isToday ? 'bg-emerald-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center' : 'text-slate-700 dark:text-slate-300'}`}>{date.getDate()}</span>
                        {dots.length > 0 && <div className="flex gap-0.5">{dots.map((dot, di) => <span key={di} className={`w-1.5 h-1.5 rounded-full ${dot.color}`} />)}</div>}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.public.slice(0, 3).map((ev, ei) => <div key={ei} className="text-[10px] text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 rounded px-1 py-0.5 truncate border-l-2 border-emerald-500">{ev.event_name || ev.title}</div>)}
                        {dayEvents.holds.slice(0, 2).map((h, hi) => <div key={hi} className="text-[10px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded px-1 py-0.5 truncate border-l-2 border-amber-500">{h.eventTitle || 'Hold'}</div>)}
                        {dayEvents.private.slice(0, 2).map((pe, pi) => <div key={pi} className="text-[10px] text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40 rounded px-1 py-0.5 truncate border-l-2 border-violet-500">{pe.title}</div>)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        
          {/* EVENT RESULTS — Below the calendar */}
          <div className="mt-4" data-testid="event-results">
            {selectedDate ? (
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</h3>
                  <button onClick={() => setSelectedDate(null)} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"><X className="h-4 w-4" /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {sidebarPublicEvents.map((ev, i) => (
                    <div key={`pub-${i}`} className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 border-l-[3px] border-l-emerald-500">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{ev.event_name || ev.title}</p>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                        {ev.start_time && <span className="flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{ev.start_time}</span>}
                        {ev.venue_name && <span className="flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{ev.venue_name}</span>}
                      </div>
                      {ev.category_info && <Badge variant="outline" className="mt-1 text-[9px] px-1.5 py-0">{ev.category_info.name}</Badge>}
                    </div>
                  ))}
                  {sidebarHolds.map((h, i) => (
                    <div key={`hold-${i}`} className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 border-l-[3px] border-l-amber-500">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{h.eventTitle || 'Hold'}</p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                        {h.hasUserHold && <Badge className="bg-blue-100 text-blue-700 text-[9px] mr-1">Your Hold</Badge>}
                        {h.holdsCount && `${h.holdsCount} hold${h.holdsCount !== 1 ? 's' : ''}`}
                      </p>
                    </div>
                  ))}
                  {sidebarPrivateEvents.map((pe, i) => (
                    <div key={`priv-${i}`} className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 cursor-pointer hover:shadow-md transition-all border-l-[3px] border-l-violet-500" onClick={() => handlePrivateEventClick(pe)}>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{pe.title}</p>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                        {pe.time && <span className="flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{pe.time}</span>}
                        {pe.location && <span className="flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{pe.location}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                {sidebarPublicEvents.length === 0 && sidebarHolds.length === 0 && sidebarPrivateEvents.length === 0 && (
                  <div className="text-center py-4 text-slate-400 dark:text-slate-500">
                    <CalendarDays className="h-6 w-6 mx-auto mb-1 text-slate-300 dark:text-slate-600" />
                    <p className="text-sm">No events on this date</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-1">Upcoming Events</h3>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-3">Click a date to see details</p>
                {upcomingPrivateEvents.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {upcomingPrivateEvents.slice(0, 6).map((pe, i) => (
                      <div key={i} className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" onClick={() => handlePrivateEventClick(pe)}>
                        <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{pe.title}</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">{pe.date} {pe.time && `at ${pe.time}`}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 dark:text-slate-500">No upcoming events</p>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      <FindSimilarEvents isOpen={findSimilarOpen} onClose={() => setFindSimilarOpen(false)} publishedEvents={publishedEvents} getAuthHeaders={getAuthHeaders} />

      <GuestActionModal open={calGuestModalOpen} onOpenChange={setCalGuestModalOpen} />

      <Dialog open={privateDetailOpen} onOpenChange={setPrivateDetailOpen}>
        <DialogContent className="max-w-lg" data-testid="private-event-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selectedPrivateEvent?.title}</DialogTitle>
            <DialogDescription>Private Event Details</DialogDescription>
          </DialogHeader>
          {selectedPrivateEvent && (
            <div className="space-y-3 text-sm">
              {selectedPrivateEvent.date && <div className="flex items-center gap-2 text-slate-600"><CalendarDays className="h-4 w-4" /><span>{selectedPrivateEvent.date}</span>{selectedPrivateEvent.time && <span>at {selectedPrivateEvent.time}</span>}</div>}
              {selectedPrivateEvent.location && <div className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4" /><span>{selectedPrivateEvent.location}</span></div>}
              {selectedPrivateEvent.description && <p className="text-slate-600">{selectedPrivateEvent.description}</p>}
              {selectedPrivateEvent.notes && <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs font-medium text-slate-500 mb-1">Notes</p><p className="text-sm text-slate-700">{selectedPrivateEvent.notes}</p></div>}
              {selectedPrivateEvent.lists?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Checklists</p>
                  {selectedPrivateEvent.lists.map((listId, i) => {
                    const list = getListById(listId);
                    return list ? (
                      <div key={i} className="flex items-center gap-2 text-xs text-slate-600 mb-1">
                        <ListTodo className="h-3 w-3" /><span>{list.name}</span>
                        <Badge variant="outline" className="text-[9px]">{list.items?.filter(it => it.completed).length}/{list.items?.length}</Badge>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Action Popup */}
      {quickActionDate && !isGuestMode && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setQuickActionDate(null)} />
          <div
            className="fixed z-50 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-2 w-56"
            style={{ left: quickActionPos.x, top: quickActionPos.y }}
            data-testid="quick-action-menu"
          >
            <p className="text-xs font-semibold text-muted-foreground px-2 py-1 mb-1">
              {quickActionDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </p>
            <button
              onClick={() => { navigateToCreateWithDate(quickActionDate, 'hold'); setQuickActionDate(null); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              data-testid="quick-create-hold"
            >
              <CalendarDays className="h-4 w-4 text-amber-500" />
              Create Hold on this date
            </button>
            <button
              onClick={() => { navigateToCreateWithDate(quickActionDate, 'private'); setQuickActionDate(null); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              data-testid="quick-create-private"
            >
              <Plus className="h-4 w-4 text-violet-500" />
              Create Private Event
            </button>
            <div className="border-t border-slate-100 dark:border-slate-700 my-1" />
            <button
              onClick={() => { togglePinDate(quickActionDate); setQuickActionDate(null); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              data-testid="quick-pin-date"
            >
              {pinnedDates.includes(quickActionDate.toISOString().split('T')[0]) ? (
                <><PinOff className="h-4 w-4 text-red-500" /> Unpin this date</>
              ) : (
                <><Pin className="h-4 w-4 text-blue-500" /> Pin this date</>
              )}
            </button>
          </div>
        </>
      )}

      {/* Pinned Dates Tray */}
      {pinnedDates.length > 0 && !isGuestMode && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-30 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center gap-3 max-w-[90vw] overflow-x-auto" data-testid="pinned-dates-tray">
          <Pin className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <span className="text-xs font-semibold text-muted-foreground flex-shrink-0">Pinned:</span>
          {pinnedDates.map(dateStr => (
            <Badge
              key={dateStr}
              variant="secondary"
              className="flex items-center gap-1 cursor-pointer hover:bg-destructive/10 flex-shrink-0"
              onClick={() => {
                setPinnedDates(prev => {
                  const updated = prev.filter(d => d !== dateStr);
                  localStorage.setItem('planblox-pinned-dates', JSON.stringify(updated));
                  return updated;
                });
              }}
            >
              {new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              <X className="h-3 w-3" />
            </Badge>
          ))}
          <Button
            variant="default"
            size="sm"
            className="text-xs flex-shrink-0"
            onClick={() => navigate(`/create?prefillDates=${pinnedDates.join(',')}&prefillType=hold`)}
            data-testid="use-pinned-dates-btn"
          >
            Use Dates
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground flex-shrink-0"
            onClick={clearPinnedDates}
            data-testid="clear-pinned-dates-btn"
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

export default CalendarView;

